/**
 * Offer Form Component
 * For tutors to make offers on assignments
 */

import React, { useState } from 'react';
import { HelpCircle, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';

const OfferForm = ({ assignment, onSubmit, isLoading }) => {
  const [price, setPrice] = useState('');
  const [message, setMessage] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!price || !message || !timeEstimate) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Check if price is within reasonable range
    const priceNum = Number(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    const offerData = {
      price: priceNum,
      message,
      timeEstimate
    };
    
    onSubmit(offerData);
  };
  
  return (
    <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Make an Offer</h3>
      
      <form onSubmit={handleSubmit}>
        {/* Price Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Price (ZAR)*
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              R
            </span>
            <input
              type="number"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., 180"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min="1"
              max={assignment?.budget ? assignment.budget * 2 : 9999}
              required
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Budget: R{assignment?.budget || 0}
          </p>
        </div>
        
        {/* Time Estimate */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Time Estimate*
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={timeEstimate}
            onChange={(e) => setTimeEstimate(e.target.value)}
            required
          >
            <option value="">Select time estimate</option>
            <option value="0-1">Less than 1 hour</option>
            <option value="1-2">1-2 hours</option>
            <option value="2-4">2-4 hours</option>
            <option value="4-8">4-8 hours</option>
            <option value="8+">8+ hours</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Student estimate: {assignment?.timeEstimate || 'Not specified'}
          </p>
        </div>
        
        {/* Message Input */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message to Student*
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[120px]"
            placeholder="Introduce yourself and explain how you can help with this assignment..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {message.length}/500 characters
          </p>
        </div>
        
        {/* Academic Integrity Reminder */}
        <div className="bg-blue-50 p-3 rounded-md mb-5 flex items-start">
          <HelpCircle className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">Academic Integrity Reminder</p>
            <p>
              When offering help, remember that your role is to guide and assist the student in understanding the assignment, not to complete the work for them. This helps maintain academic integrity while still providing valuable support.
            </p>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-md hover:from-green-600 hover:to-teal-600 transition shadow-sm flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                Submitting...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Submit Offer
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfferForm;